/* URBRAND Header & Mobile Navigation — v5.9.78 */
(() => {
  'use strict';

  const controllers = new WeakMap();
  const focusableSelector = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  class HeaderController {
    constructor(system) {
      this.system = system;
      this.header = system.querySelector('[data-header-root]');
      this.sectionId = system.dataset.headerSection;
      this.mobileMenu = system.querySelector(`[data-mobile-menu][data-header-section="${this.sectionId}"]`);
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.openMegaItem = null;
      this.lastTrigger = null;
      this.hoverTimer = 0;
      this.lastScrollY = window.scrollY;
      this.ticking = false;
      this.resizeObserver = null;
      this.mobileViewport = window.matchMedia('(max-width: 989px)');

      if (!this.header) return;
      this.bind();
      this.syncHeaderHeight();
      this.onScroll();
    }

    bind() {
      const opts = { signal: this.signal };
      this.bindActionTooltips(opts);

      this.system.addEventListener('click', (event) => this.onClick(event), opts);
      this.system.addEventListener('keydown', (event) => this.onKeydown(event), opts);
      document.addEventListener('click', (event) => this.onDocumentClick(event), opts);
      window.addEventListener('scroll', () => this.requestScrollUpdate(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.onResize(), { passive: true, signal: this.signal });

      const desktopMenuTrigger = this.system.dataset.menuTrigger || this.header.dataset.menuTrigger || 'hover';
      if (desktopMenuTrigger === 'hover') {
        this.system.querySelectorAll('[data-mega-item]').forEach((item) => {
          item.addEventListener('mouseenter', () => this.scheduleMegaOpen(item), opts);
          item.addEventListener('mouseleave', () => this.scheduleMegaClose(item), opts);
          item.addEventListener('focusin', () => this.openMega(item), opts);
          item.addEventListener('focusout', (event) => {
            if (!item.contains(event.relatedTarget)) this.closeMega(item);
          }, opts);
        });
      }

      this.mobileMenu?.addEventListener('keydown', (event) => this.trapMobileFocus(event), opts);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncHeaderHeight());
        this.resizeObserver.observe(this.header);
      }
    }

    bindActionTooltips(opts) {
      this.header.querySelectorAll('[data-header-tooltip]').forEach((action) => {
        const reset = () => action.removeAttribute('data-tooltip-dismissed');
        action.addEventListener('pointerenter', reset, opts);
        action.addEventListener('pointerleave', reset, opts);
        action.addEventListener('focusin', reset, opts);
        action.addEventListener('focusout', reset, opts);
        action.addEventListener('click', () => action.setAttribute('data-tooltip-dismissed', ''), opts);
      });
    }

    onClick(event) {
      const openButton = event.target.closest('[data-menu-open]');
      if (openButton && this.system.contains(openButton)) {
        event.preventDefault();
        this.openMobile(openButton);
        return;
      }

      const closeButton = event.target.closest('[data-menu-close]');
      if (closeButton && this.system.contains(closeButton)) {
        event.preventDefault();
        this.closeMobile();
        return;
      }

      const megaToggle = event.target.closest('[data-mega-toggle]');
      if (megaToggle && this.header.contains(megaToggle)) {
        event.preventDefault();
        const item = megaToggle.closest('[data-mega-item]');
        if (item === this.openMegaItem) this.closeMega(item, true);
        else this.openMega(item);
        return;
      }

      const nextButton = event.target.closest('[data-mobile-next]');
      if (nextButton && this.mobileMenu?.contains(nextButton)) {
        event.preventDefault();
        this.showMobilePanel(nextButton.dataset.mobileNext, nextButton);
        return;
      }

      const backButton = event.target.closest('[data-mobile-back]');
      if (backButton && this.mobileMenu?.contains(backButton)) {
        event.preventDefault();
        this.showMobilePanel(backButton.dataset.mobileBack, backButton);
        return;
      }

      const accordionSummary = event.target.closest('[data-mobile-accordion] > summary');
      if (accordionSummary) {
        const current = accordionSummary.parentElement;
        const list = current?.parentElement;
        if (list) {
          list.querySelectorAll(':scope > li > details[open], :scope > details[open]').forEach((details) => {
            if (details !== current) details.removeAttribute('open');
          });
        }
      }

      if (event.target.closest('[data-search-open], [data-wishlist-open], [data-cart-open]')) {
        this.closeMobile(false);
      }

      if (this.system.dataset.mobileCloseOnLink === 'true' && event.target.closest('[data-mobile-link]')) {
        this.closeMobile(false);
      }
    }

    onDocumentClick(event) {
      if (this.openMegaItem && !this.header.contains(event.target)) this.closeMega(this.openMegaItem);
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.header.querySelectorAll('[data-header-tooltip]').forEach((action) => {
        action.setAttribute('data-tooltip-dismissed', '');
      });
      if (this.mobileMenu?.classList.contains('is-open')) {
        event.preventDefault();
        this.closeMobile(true);
        return;
      }
      if (this.openMegaItem) {
        event.preventDefault();
        const button = this.openMegaItem.querySelector('[data-mega-toggle]');
        this.closeMega(this.openMegaItem);
        button?.focus();
      }
    }

    scheduleMegaOpen(item) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = window.setTimeout(() => this.openMega(item), 90);
    }

    scheduleMegaClose(item) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = window.setTimeout(() => this.closeMega(item), 150);
    }

    openMega(item) {
      if (!item || window.matchMedia('(max-width: 989px)').matches) return;
      window.clearTimeout(this.hoverTimer);
      if (this.openMegaItem && this.openMegaItem !== item) this.closeMega(this.openMegaItem);
      const menu = item.querySelector('[data-mega-menu]');
      const button = item.querySelector('[data-mega-toggle]');
      if (!menu || !button) return;
      menu.hidden = false;
      item.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');
      this.openMegaItem = item;
    }

    closeMega(item, restoreFocus = false) {
      if (!item) return;
      window.clearTimeout(this.hoverTimer);
      const menu = item.querySelector('[data-mega-menu]');
      const button = item.querySelector('[data-mega-toggle]');
      if (menu) menu.hidden = true;
      item.classList.remove('is-open');
      button?.setAttribute('aria-expanded', 'false');
      if (this.openMegaItem === item) this.openMegaItem = null;
      if (restoreFocus) button?.focus();
    }

    openMobile(trigger) {
      if (!this.mobileMenu || !this.mobileViewport.matches) return;
      this.closeMega(this.openMegaItem);
      this.lastTrigger = trigger || document.activeElement;
      this.mobileMenu.removeAttribute('inert');
      this.mobileMenu.classList.add('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('menu-open');
      this.system.querySelectorAll('[data-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      this.resetMobilePanels();
      window.requestAnimationFrame(() => {
        const autofocus = this.mobileMenu.querySelector('[data-autofocus]');
        (autofocus || this.mobileMenu.querySelector(focusableSelector))?.focus({ preventScroll: true });
      });
    }

    closeMobile(restoreFocus = true) {
      if (!this.mobileMenu?.classList.contains('is-open')) return;
      this.mobileMenu.classList.remove('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      this.mobileMenu.setAttribute('inert', '');
      document.body.classList.remove('menu-open');
      this.system.querySelectorAll('[data-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.mobileMenu.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
      this.resetMobilePanels();
      if (restoreFocus && this.lastTrigger instanceof HTMLElement) this.lastTrigger.focus({ preventScroll: true });
    }

    resetMobilePanels() {
      if (!this.mobileMenu) return;
      this.mobileMenu.querySelectorAll('[data-mobile-panel]').forEach((panel) => {
        const active = panel.dataset.mobilePanel === 'root';
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    }

    showMobilePanel(name, trigger) {
      if (!this.mobileMenu || !name) return;
      const panels = [...this.mobileMenu.querySelectorAll('[data-mobile-panel]')];
      const next = panels.find((panel) => panel.dataset.mobilePanel === name);
      if (!next) return;
      panels.forEach((panel) => {
        const active = panel === next;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      next.querySelector('[data-mobile-back], .mobile-nav__panel-title, a, button')?.focus({ preventScroll: true });
      const scroll = this.mobileMenu.querySelector('.mobile-menu__scroll');
      if (scroll) scroll.scrollTop = 0;
      if (trigger) next.dataset.returnFocus = trigger.dataset.mobileNext || trigger.dataset.mobileBack || '';
    }

    trapMobileFocus(event) {
      if (event.key !== 'Tab' || !this.mobileMenu?.classList.contains('is-open')) return;
      const focusables = [...this.mobileMenu.querySelectorAll(focusableSelector)].filter((node) => {
        return !node.hidden && node.offsetParent !== null && node.getAttribute('aria-hidden') !== 'true';
      });
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    requestScrollUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.onScroll();
        this.ticking = false;
      });
    }

    onScroll() {
      const y = Math.max(0, window.scrollY);
      const behavior = this.system.dataset.stickyBehavior || 'always';
      const scrolled = y > 20;
      const overlayOpen = document.body.classList.contains('menu-open') || Boolean(document.querySelector('dialog[open]'));
      this.header.classList.toggle('is-scrolled', scrolled);
      if (behavior === 'scroll_up' && !overlayOpen) {
        const down = y > this.lastScrollY + 4 && y > 180;
        const up = y < this.lastScrollY - 4;
        if (down) {
          this.header.classList.add('is-hidden');
          this.system.classList.add('is-header-hidden');
        }
        if (up || y < 80) {
          this.header.classList.remove('is-hidden');
          this.system.classList.remove('is-header-hidden');
        }
      } else {
        this.header.classList.remove('is-hidden');
        this.system.classList.remove('is-header-hidden');
      }
      this.lastScrollY = y;
    }

    onResize() {
      this.syncHeaderHeight();
      if (!this.mobileViewport.matches) this.closeMobile(false);
      else if (this.openMegaItem) this.closeMega(this.openMegaItem);
    }

    syncHeaderHeight() {
      const height = Math.round(this.header.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty('--site-header-height', `${height}px`);
    }

    destroy() {
      window.clearTimeout(this.hoverTimer);
      this.resizeObserver?.disconnect();
      this.abort.abort();
      this.closeMobile(false);
      this.closeMega(this.openMegaItem);
      this.system.classList.remove('is-header-hidden');
    }
  }

  const init = (scope = document) => {
    const systems = scope.matches?.('[data-header-system]') ? [scope] : [...scope.querySelectorAll('[data-header-system]')];
    systems.forEach((system) => {
      controllers.get(system)?.destroy();
      controllers.set(system, new HeaderController(system));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-header-system]').forEach((system) => {
      controllers.get(system)?.destroy();
      controllers.delete(system);
    });
  });
})();
